/**
 * Komal OS - CRM dashboard: where the prospects are.
 *
 * Every customer account counts as a prospect (2026-09-19 decision), so
 * this is a plain count of accounts - no won/lost filtering. Owner-scoped
 * server-side like the rest of the dashboard (see get_dashboard_geography
 * in app/routes/crm_routes.py): a salesperson sees their own accounts, the
 * CEO sees everyone's.
 *
 * Region bars + a world map (countries shaded, or a bubble per city), with
 * a ranked table under them that carries every number the map shows - the
 * map's tooltip is a convenience, never the only way to read a value.
 */

import React, { Suspense, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Globe2, MapPinOff } from 'lucide-react';
import { getDashboardGeography } from '../../services/crmService';
import useAuthStore from '../../store/authStore';

const WorldMap = React.lazy(() => import('./WorldMap')); // ~750KB of map shapes - load on demand

const BAR = '#2a78d6';
const BAR_UNKNOWN = '#c3c2b7'; // "Unknown" is a data-quality bucket, not a region: emphasis form, greyed
const TABLE_ROWS_COLLAPSED = 8;

const pct = (n, total) => (total ? Math.round((n / total) * 100) : 0);

function RegionBars({ regions, total }) {
  const max = Math.max(1, ...regions.map((r) => r.count));
  return (
    <div className="space-y-2.5">
      {regions.map((r) => (
        <div key={r.region} className="flex items-center gap-3" title={`${r.region}: ${r.count} prospect${r.count === 1 ? '' : 's'} (${pct(r.count, total)}%)`}>
          <span className={`w-32 shrink-0 text-xs truncate ${r.region === 'Unknown' ? 'text-gray-400 italic' : 'text-gray-600'}`}>{r.region}</span>
          <div className="flex-1 flex items-center min-w-0">
            <div
              className="h-4 hover:opacity-80"
              style={{
                width: `${Math.max((r.count / max) * 84, 1.5)}%`,
                backgroundColor: r.region === 'Unknown' ? BAR_UNKNOWN : BAR,
                borderRadius: '0 4px 4px 0', // square at the baseline, rounded at the data end
              }}
            />
            <span className="ml-2 text-xs font-bold text-gray-800 tabular-nums">{r.count}</span>
            <span className="ml-1.5 text-[11px] text-gray-400 tabular-nums">{pct(r.count, total)}%</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function RankedTable({ mode, countries, cities, total, onFocusCity }) {
  const [expanded, setExpanded] = useState(false);
  const rows = mode === 'country' ? countries : cities;
  const shown = expanded ? rows : rows.slice(0, TABLE_ROWS_COLLAPSED);

  if (rows.length === 0) {
    return <p className="text-xs text-gray-400 py-3">{mode === 'country' ? 'No prospect has a country yet.' : 'No prospect city could be placed on the map yet.'}</p>;
  }

  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400 border-b border-gray-100">
              <th className="pb-2 font-semibold">{mode === 'country' ? 'Country' : 'City'}</th>
              <th className="pb-2 font-semibold">{mode === 'country' ? 'Region' : 'Country'}</th>
              <th className="pb-2 font-semibold text-right">Prospects</th>
              <th className="pb-2 font-semibold text-right">Share</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {shown.map((r) => (
              <tr
                key={mode === 'country' ? r.code : `${r.code}-${r.city}`}
                className={mode === 'city' ? 'cursor-pointer hover:bg-gray-50' : ''}
                onClick={mode === 'city' ? () => onFocusCity({ lng: r.lng, lat: r.lat, at: Date.now() }) : undefined}
                title={mode === 'city' ? 'Zoom the map to this city' : undefined}
              >
                <td className="py-2 font-medium text-gray-800">
                  {mode === 'country' ? r.country : r.city}
                  {mode === 'city' && r.approx && <span className="ml-1.5 text-[10px] font-semibold text-gray-400" title="Placed by state, not by the city itself">≈ approx.</span>}
                </td>
                <td className="py-2 text-gray-500">{mode === 'country' ? r.region : r.country}</td>
                <td className="py-2 text-right font-semibold text-gray-800 tabular-nums">{r.count}</td>
                <td className="py-2 text-right text-gray-400 tabular-nums">{pct(r.count, total)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length > TABLE_ROWS_COLLAPSED && (
        <button onClick={() => setExpanded((v) => !v)} className="mt-2 text-xs font-semibold text-komal-blue hover:underline">
          {expanded ? 'Show fewer' : `Show all ${rows.length}`}
        </button>
      )}
    </>
  );
}

export default function GeographyPanel() {
  const { user } = useAuthStore();
  const [geo, setGeo] = useState(null);
  const [failed, setFailed] = useState(false);
  const [mode, setMode] = useState('country'); // 'country' | 'city'
  const [focus, setFocus] = useState(null);

  useEffect(() => {
    getDashboardGeography().then((res) => setGeo(res.data)).catch(() => setFailed(true));
  }, []);

  if (failed) return null; // the rest of the dashboard still stands on its own
  if (!geo) {
    return (
      <div className="glass-card p-5 mb-5">
        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Prospects by region</div>
        <p className="text-sm text-gray-400 mt-3">Loading…</p>
      </div>
    );
  }

  const { total, regions, countries, cities, gaps } = geo;
  const realRegions = regions.filter((r) => r.region !== 'Unknown');
  const isCeo = user?.role === 'CEO';

  const switchMode = (next) => { setMode(next); setFocus(null); };

  return (
    <div className="glass-card p-5 mb-5">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wide"><Globe2 size={13} /> Prospects by region</div>
          <div className="flex items-baseline gap-2 mt-1.5">
            <span className="text-4xl font-extrabold text-gray-900 leading-none">{total}</span>
            <span className="text-sm text-gray-500">
              prospect{total === 1 ? '' : 's'}
              {total > 0 && <> · {countries.length} countr{countries.length === 1 ? 'y' : 'ies'} · {realRegions.length} region{realRegions.length === 1 ? '' : 's'}</>}
            </span>
          </div>
          <p className="text-[11px] text-gray-400 mt-1">Every customer account counts as a prospect.</p>
        </div>

        <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden text-xs font-semibold" role="group" aria-label="Map view">
          {[['country', 'Countries'], ['city', 'Cities']].map(([key, label]) => (
            <button
              key={key}
              type="button"
              aria-pressed={mode === key}
              onClick={() => switchMode(key)}
              className={`px-3.5 py-1.5 ${mode === key ? 'bg-komal-blue text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {total === 0 ? (
        <p className="text-sm text-gray-400 py-6 text-center">No customers yet — scan a card or import a list to see where they are.</p>
      ) : (
        <>
          <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.7fr)]">
            <div>
              <div className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-3">Prospects per region</div>
              <RegionBars regions={regions} total={total} />
            </div>
            <Suspense fallback={<div className="rounded-lg bg-gray-50 text-sm text-gray-400 flex items-center justify-center" style={{ aspectRatio: '800 / 410' }}>Loading map…</div>}>
              <WorldMap mode={mode} countries={countries} cities={cities} focus={focus} />
            </Suspense>
          </div>

          <div className="mt-5 pt-4 border-t border-gray-100">
            <div className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-2">
              {mode === 'country' ? 'Countries, ranked' : 'Cities, ranked — click one to zoom the map'}
            </div>
            <RankedTable mode={mode} countries={countries} cities={cities} total={total} onFocusCity={setFocus} />
          </div>

          {(gaps.no_country > 0 || gaps.unplaced.length > 0) && (
            <div className="mt-4 flex gap-2.5 items-start bg-amber-50 border border-amber-200 rounded-lg px-3.5 py-3 text-xs text-amber-900">
              <MapPinOff size={14} className="shrink-0 mt-0.5" />
              <div className="space-y-1">
                {gaps.no_country > 0 && (
                  <p>
                    <span className="font-semibold">{gaps.no_country} prospect{gaps.no_country === 1 ? ' has' : 's have'} no country yet</span> and {gaps.no_country === 1 ? 'shows' : 'show'} as Unknown.{' '}
                    {isCeo
                      ? <>Press <span className="font-semibold">Fill locations</span> on the Customers page, or <Link to="/crm/accounts?missing=country" className="underline font-semibold">see which ones are missing a country</Link> and edit them.</>
                      : <><Link to="/crm/accounts?missing=country" className="underline font-semibold">See which ones</Link> and add a country on each customer's page — or ask the CEO to run <span className="font-semibold">Fill locations</span>.</>}
                  </p>
                )}
                {gaps.unplaced.length > 0 && (
                  <p>
                    <span className="font-semibold">Couldn't place on the map:</span>{' '}
                    {gaps.unplaced.slice(0, 5).map((u) => `${u.city}${u.country ? ` (${u.country})` : ''}${u.count > 1 ? ` ×${u.count}` : ''}`).join(', ')}
                    {gaps.unplaced.length > 5 ? ` and ${gaps.unplaced.length - 5} more` : ''} — still counted in the country totals.
                  </p>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
